import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, Search, Menu, X, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import CartSheet from "@/components/CartSheet";

const NAV = [
  { to: "/shop", label: "Shop All" },
  { to: "/shop?tag=shabbos", label: "Shabbos" },
  { to: "/shop?tag=yom-tov", label: "Yom Tov" },
  { to: "/shop/Dresses", label: "Dresses" },
  { to: "/shop/Skirts", label: "Skirts" },
  { to: "/shop/Head Coverings", label: "Head Coverings" },
  { to: "/shop/Men", label: "Men" },
  { to: "/vendors", label: "Vendors" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const { count: wishCount } = useWishlist() || { count: 0 };
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Announcement */}
      <div className="bg-[#1A1A1A] text-[#F7F5F0] py-2 text-center label-uppercase">
        <span data-testid="announcement-bar">Complimentary shipping on orders $150+ · Sourced from Jewish-owned ateliers worldwide</span>
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-40 transition-colors duration-300 ${scrolled ? "bg-[#F7F5F0]/95 backdrop-blur border-b border-[#EAE5D9]" : "bg-[#F7F5F0]"}`}>
        <div className="container-editorial flex items-center justify-between py-5">
          <button data-testid="mobile-menu-toggle" className="lg:hidden" onClick={() => setMenuOpen(true)} aria-label="menu">
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" data-testid="brand-logo" className="font-serif text-3xl md:text-4xl tracking-tighter font-semibold">
            Kesher<span className="text-[#B24C38]">.</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map(item => (
              <NavLink key={item.to} to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={({isActive}) => `label-uppercase link-underline ${isActive ? "text-[#B24C38]" : "text-[#111111]"}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-5">
            <Link to="/shop" aria-label="search" data-testid="header-search">
              <Search className="w-5 h-5" />
            </Link>
            <Link to="/wishlist" data-testid="header-wishlist" aria-label="wishlist" className="relative">
              <Heart className="w-5 h-5" />
              {wishCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#2C402E] text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-semibold">
                  {wishCount}
                </span>
              )}
            </Link>
            {user?.role === "vendor" ? (
              <Link to="/vendor/dashboard" data-testid="header-vendor-dashboard" className="label-uppercase hidden md:inline link-underline text-[#B24C38]">
                Dashboard
              </Link>
            ) : (
              <Link to="/vendor/apply" data-testid="header-sell-cta" className="label-uppercase hidden md:inline link-underline">
                Sell on Kesher
              </Link>
            )}
            {user && (
              <Link to="/orders" data-testid="header-orders" className="label-uppercase hidden md:inline link-underline">
                Orders
              </Link>
            )}
            {user ? (
              <button data-testid="header-logout" onClick={logout} className="label-uppercase hidden md:inline">Logout</button>
            ) : (
              <Link to="/login" data-testid="header-login" className="label-uppercase hidden md:inline link-underline">Sign In</Link>
            )}
            <button data-testid="header-cart" onClick={() => setOpen(true)} className="relative">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#B24C38] text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-semibold">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 bg-[#F7F5F0] lg:hidden">
          <div className="container-editorial py-6 flex justify-between items-center">
            <span className="font-serif text-3xl">Kesher.</span>
            <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)}><X className="w-6 h-6" /></button>
          </div>
          <div className="container-editorial flex flex-col gap-6 mt-10">
            {NAV.map(item => (
              <Link key={item.to} to={item.to} className="font-serif text-3xl">{item.label}</Link>
            ))}
            <div className="border-t border-[#EAE5D9] pt-6 mt-4">
              {user ? (
                <button onClick={logout} className="label-uppercase">Logout</button>
              ) : (
                <Link to="/login" className="label-uppercase">Sign In</Link>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="flex-1"><Outlet /></main>

      <CartSheet />

      <footer className="bg-[#1A1A1A] text-[#F7F5F0] mt-32">
        <div className="container-editorial py-24">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="md:col-span-2">
              <p className="label-uppercase text-[#D4A373] mb-4">The Marketplace</p>
              <h3 className="font-serif text-4xl md:text-5xl tracking-tighter leading-none">
                Modest clothing, sourced from Jewish ateliers.
              </h3>
            </div>
            <div>
              <p className="label-uppercase mb-4 text-[#D4A373]">Shop</p>
              <ul className="space-y-2 text-sm">
                <li><Link to="/shop">All Products</Link></li>
                <li><Link to="/shop/Dresses">Dresses</Link></li>
                <li><Link to="/shop/Skirts">Skirts</Link></li>
                <li><Link to="/vendors">All Vendors</Link></li>
              </ul>
            </div>
            <div>
              <p className="label-uppercase mb-4 text-[#D4A373]">Support</p>
              <ul className="space-y-2 text-sm">
                <li>Shipping</li>
                <li>Returns</li>
                <li>Sizing</li>
                <li>Contact</li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-[#3a3a3a] flex justify-between items-end">
            <p className="font-serif leading-none tracking-tighter" style={{ fontSize: "clamp(64px, 12vw, 200px)" }}>
              Kesher<span className="text-[#B24C38]">.</span>
            </p>
          </div>
          <p className="mt-8 text-xs text-[#a9a9a9]">© {new Date().getFullYear()} Kesher. Every vendor verified Jewish-owned.</p>
        </div>
      </footer>
    </div>
  );
}
